/**
 * Primary auth hook — TanStack Query + Zustand orchestration.
 * Consumed by all screens needing auth state.
 */
import { useCallback, useEffect } from 'react'
import { AppState } from 'react-native'
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'
import i18n, { shouldApplyProfileLocale } from '../i18n/config'
import { supabase } from '../supabase'
import { authService } from '../services/auth.service'
import { phoneVerificationService } from '../services/phone-verification.service'
import { ServiceError } from '../errors/service-error'
import { securityTelemetryService } from '../services/security-telemetry.service'
import { useAuthStore } from '../stores/auth.store'
import {
  resetClientState,
  conciliarDuenoDeBorradores,
  type MotivoDeCierre,
} from '../stores/reset-all'
import type { LoginInput, RegisterInput, ForgotPasswordInput } from '../validations/auth.schema'
import { addBreadcrumb } from '../utils/observability'

// Query key factory
export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  profile: (userId: string) => [...authKeys.all, 'profile', userId] as const,
}

// ─── Module-level auth subscription + SIGNED_OUT dedupe ─────────────────────
// `useAuth()` is called from many components (AuthGate, AppTopNav, ProfileScreen,
// plus any screen that shows the current user). Auth itself is app-global, so
// only one Supabase listener should exist per app runtime. The ref-count below
// lets every hook consumer mount/unmount normally while keeping exactly one
// subscription alive.
//
// `userInitiatedSignOut` is set to `true` right before `signOut()` is called
// and stays true until the next SIGNED_IN. We do NOT consume it on the first
// subscriber's pass (that was the old bug — the first subscriber would reset
// the flag, and every subsequent subscriber would see `false` and treat the
// event as an unexpected expiry, firing the "sesión expirada" toast even
// though the user just clicked logout themselves). Reset only happens on
// SIGNED_IN/INITIAL_SESSION so the *next* logout correctly distinguishes
// intentional vs token-refresh failure.
//
// `lastSessionExpiredHandledAt` lets multiple subscribers reach the same
// branch but only the first one within a 5s window actually mutates state /
// fires the banner. Dedupe is per-event-fan-out, not per-mutation.
let userInitiatedSignOut = false
let lastSessionExpiredHandledAt = 0
let hadAuthenticatedSession = false
let authSubscriberCount = 0
let authSubscriptionCleanup: (() => void) | null = null
// The last id we saw an authenticated session for. Lets the SIGNED_IN branch
// detect a no-sign-out account SWITCH (a direct SIGNED_IN for a different user,
// which never emits SIGNED_OUT) and wipe the previous account's scoped caches
// before seeding the new one — so account A's data can never render under B.
let lastAuthenticatedUserId: string | null = null
// Foreground reconciler rate-limit + reentrancy guards (see reconcileSessionCache).
let reconcileInFlight = false
let lastReconcileAt = 0
const RECONCILE_MIN_INTERVAL_MS = 3000

// Public/cross-session query roots preserved through an account boundary (the
// explore catalog, hosting-wizard lookups, geo/catalog reference data). Every
// OTHER root is user-scoped and dropped. Shared by SIGNED_OUT and the account
// switch so the two reset paths can never drift apart.
const PUBLIC_QUERY_KEYS = new Set<string>([
  'vehicles',
  'vehicle-detail',
  'categories',
  'makes',
  'models',
  'cities',
  'countries',
  'regions',
  'cancellation-policies',
])

// Drop every user-scoped query + Zustand store so no prior-account data leaks
// across an identity boundary. Whitelist inversion: anything not explicitly
// public is user-scoped and removed (a newly-added user hook is covered without
// touching this list).
//
// IMPORTANT: 'auth' is NOT public, so this removes the entire ['auth'] root —
// which INCLUDES authKeys.session() and authKeys.profile(). That is intentional
// and load-bearing:
//   • SIGNED_OUT relies on this removal as its session-clear (it has no explicit
//     setQueryData(session, null)); this is the one path that logs the user out.
//   • The account-switch and foreground-reconcile paths re-seed the session
//     synchronously on the very next line, so no observer ever sees a blank
//     session there.
// Therefore 'auth' must NEVER be added to PUBLIC_QUERY_KEYS — doing so would
// silently break logout (isAuthenticated would stay true after sign-out).
// It does NOT touch the session-expired banner, userInitiatedSignOut, or the
// initializing flag — those are sequenced per-caller (sign-out vs switch).
function wipeAccountScopedCaches(queryClient: QueryClient, motivo: MotivoDeCierre): void {
  queryClient
    .getQueryCache()
    .getAll()
    .forEach((cache) => {
      const root = cache.queryKey[0]
      if (typeof root === 'string' && !PUBLIC_QUERY_KEYS.has(root)) {
        queryClient.removeQueries({ queryKey: [root] })
      }
    })
  // Favorites are a sub-key of the preserved 'vehicles' root — drop explicitly.
  queryClient.removeQueries({ queryKey: ['vehicles', 'favorites'] })
  securityTelemetryService.resetRuntimeSession()
  resetClientState(motivo)
}

// Foreground/self-heal reconciler: re-read the LIVE client session and promote
// the session cache to it if they diverged. This is the read-side guarantee
// that the displayed identity always equals the live token — a missed/late auth
// event can no longer leave the cache permanently stale.
//
// Safety invariants (must hold — see the auth listener's DO-NOT-BREAK notes):
//  - NEVER writes null over a live session. getSession() can return null on a
//    SecureStore lag or an invalid-refresh-token wipe; that is treated as "no
//    change", NOT a logout. SIGNED_OUT remains the only path that clears it.
//  - Write-only-on-diff + in-flight + min-interval guards prevent rerender/refetch storms.
//  - Never throws into the AppState handler.
async function reconcileSessionCache(queryClient: QueryClient): Promise<void> {
  if (reconcileInFlight) return
  const now = Date.now()
  if (now - lastReconcileAt < RECONCILE_MIN_INTERVAL_MS) return
  reconcileInFlight = true
  lastReconcileAt = now
  try {
    const live = await authService.getSession()
    if (!live?.user) return // no live session → leave the cache untouched
    // Sync the switch tracker to the live identity on EVERY reconcile — even
    // when the cache already matches and we early-return just below. Otherwise a
    // lost-INITIAL_SESSION cold start (where sessionQuery's own queryFn preseeds
    // authKeys.session() without touching the tracker) leaves it null, and a
    // later genuine account switch would skip its wipe (the SIGNED_IN guard needs
    // a non-null tracker to recognize the change). The wipe below keys off the
    // CACHED id, not this tracker, so setting it here cannot suppress a wipe.
    lastAuthenticatedUserId = live.user.id
    const cached = queryClient.getQueryData<Session | null>(authKeys.session())
    if (cached?.user?.id === live.user.id && cached?.access_token === live.access_token) {
      return // identical → no write, no rerender
    }
    const idChanged = cached?.user?.id != null && cached.user.id !== live.user.id
    if (idChanged) {
      // A no-sign-out account switch whose SIGNED_IN we MISSED (the exact case
      // this reconciler heals): wipe the previous account's scoped caches/stores
      // BEFORE promoting, exactly as the SIGNED_IN switch path does — otherwise
      // account A's booking draft / messages / payment state / favorites would
      // linger under B via this read path. wipeAccountScopedCaches also drops the
      // ['auth'] root; we re-seed the session synchronously on the next line, so
      // no observer sees a gap.
      // Identidad distinta: los bytes de la anterior se van con ella.
      wipeAccountScopedCaches(queryClient, 'salida')
    }
    queryClient.setQueryData(authKeys.session(), live)
    // Tracker already synced above (before the early-return). Refetch the
    // now-correct profile only when the identity actually changed.
    if (idChanged) {
      queryClient.invalidateQueries({ queryKey: authKeys.profile(live.user.id) })
    }
  } catch {
    // Best-effort: a foreground reconcile must never surface an error.
  } finally {
    reconcileInFlight = false
  }
}

function authProvider(
  session: { user?: { app_metadata?: Record<string, unknown> } } | null,
): string | null {
  const provider = session?.user?.app_metadata?.provider
  return typeof provider === 'string' ? provider : null
}

const INITIAL_SESSION_TIMEOUT_MS = 7000

export function useAuth() {
  const queryClient = useQueryClient()
  const {
    isInitializing,
    setInitializing,
    setSessionExpired,
    setCompletingSignup,
    reset: resetStore,
  } = useAuthStore()

  // Session query (managed by auth events, not polling)
  const sessionQuery = useQuery({
    queryKey: authKeys.session(),
    queryFn: () => authService.getSession(),
    staleTime: Infinity,
    retry: false,
  })

  const user = sessionQuery.data?.user ?? null
  const isAuthenticated = !!user

  // Profile query (only when authenticated)
  const profileQuery = useQuery({
    queryKey: authKeys.profile(user?.id ?? ''),
    queryFn: () => authService.getProfile(user!.id),
    enabled: !!user,
    // Profile changes through app mutations or admin verification flows, both
    // of which invalidate/push updates explicitly. Keeping it warm avoids a
    // subtle "every screen refetches my account" feeling during navigation.
    staleTime: 10 * 60 * 1000,
  })

  useEffect(() => {
    if (!user?.id) return undefined

    const channel = supabase
      .channel(`profile-status:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: authKeys.profile(user.id) })
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [queryClient, user?.id])

  // Sync i18n language with user's preferred locale from profile
  useEffect(() => {
    const locale = profileQuery.data?.preferredLocale
    if (locale && shouldApplyProfileLocale(locale)) {
      void i18n.changeLanguage(locale)
    }
  }, [profileQuery.data?.preferredLocale])

  // Supabase normally emits INITIAL_SESSION after reading persisted auth
  // storage. If that event is lost or delayed by a native storage edge case,
  // never leave the whole app behind the splash forever.
  useEffect(() => {
    if (!isInitializing) return undefined

    const timeout = setTimeout(() => {
      if (!useAuthStore.getState().isInitializing) return
      addBreadcrumb('auth', 'Initial session timed out; releasing startup gate', {
        timeoutMs: INITIAL_SESSION_TIMEOUT_MS,
      })
      setInitializing(false)
      // The event was lost/late — don't proceed on a possibly stale/null cache.
      // Re-read the live session and seed it (null-safe: never clears the cache).
      void reconcileSessionCache(queryClient)
    }, INITIAL_SESSION_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [isInitializing, setInitializing, queryClient])

  // Auth state listener
  useEffect(() => {
    authSubscriberCount += 1

    if (!authSubscriptionCleanup) {
      const {
        data: { subscription },
      } = authService.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          if (session?.user) hadAuthenticatedSession = true
          // Reset the intentional-signout flag whenever we get a fresh session
          // — this is the only place that flips it back to false, ensuring it
          // stays "true" across the multi-subscriber SIGNED_OUT fan-out.
          userInitiatedSignOut = false
          setSessionExpired(false)
          setInitializing(false)
          // La ventana post-auth arranca acá y el guard de OTA la respeta: una
          // recarga a los 500 ms de aterrizar en /explore se ve como "el login
          // se hizo dos veces". Solo SIGNED_IN: un TOKEN_REFRESHED horario no
          // puede reiniciar la ventana y dejar la OTA esperando para siempre.
          if (event === 'SIGNED_IN' && session?.user) {
            useAuthStore.getState().markAuthenticated(Date.now())
          }
          // No-sign-out account SWITCH: signing into a DIFFERENT account emits
          // SIGNED_IN (never SIGNED_OUT), so the previous account's user-scoped
          // caches/stores would otherwise linger under the new token — account A
          // rendering under B. Wipe them BEFORE seeding the new session.
          // Strictly gated on a real id change: the first-ever sign-in
          // (lastAuthenticatedUserId === null) only records the id, and a
          // same-user TOKEN_REFRESHED/USER_UPDATED has equal ids so nothing is
          // wiped and the SIGNED_OUT banner/dedupe machinery is never entered.
          if (session?.user) {
            if (lastAuthenticatedUserId !== null && lastAuthenticatedUserId !== session.user.id) {
              wipeAccountScopedCaches(queryClient, 'salida')
            }
            lastAuthenticatedUserId = session.user.id
            // Y la comprobacion que sobrevive a que muera el proceso.
            conciliarDuenoDeBorradores(session.user.id)
          }
          queryClient.setQueryData(authKeys.session(), session)
          if (session?.user) {
            const securityEvent =
              event === 'SIGNED_IN'
                ? 'signed_in'
                : event === 'TOKEN_REFRESHED'
                  ? 'token_refreshed'
                  : 'user_updated'
            securityTelemetryService.recordAuthEvent(
              securityEvent,
              session.user.id,
              authProvider(session),
            )
            queryClient.invalidateQueries({ queryKey: authKeys.profile(session.user.id) })
          }
        }

        if (event === 'SIGNED_OUT') {
          const wasAuthenticated = hadAuthenticatedSession
          hadAuthenticatedSession = false
          // Drop every user-scoped query + Zustand store via the shared helper
          // (whitelist inversion: anything not in PUBLIC_QUERY_KEYS is dropped,
          // so a newly-added user hook is covered automatically). The SAME
          // helper runs on a no-sign-out account switch above, so the two reset
          // paths can never drift apart. This closed a real defect where a
          // persistent error toast / MMKV booking drafts raised under account A
          // survived into account B's first render.
          /*
           * LOS DOS HECHOS QUE LLEGAN POR ESTA MISMA RAMA.
           *
           * "Cerre sesion" y "se me vencio la sesion" son indistinguibles aca
           * salvo por `userInitiatedSignOut` -- la misma bandera que unas
           * lineas mas abajo decide si mostrar el cartel de sesion vencida. Que
           * ya se usara para eso y NO para esto es la razon de que una sesion
           * caida borrara quince fotos de una entrega a medio hacer.
           *
           * Salida voluntaria: se va todo, incluidos los bytes.
           * Sesion vencida: la misma persona vuelve en un minuto, y su trabajo
           * a medias tiene que estar donde lo dejo.
           */
          wipeAccountScopedCaches(queryClient, userInitiatedSignOut ? 'salida' : 'sesion-vencida')
          lastAuthenticatedUserId = null
          resetStore()
          // resetStore sets isInitializing back to true (initial state),
          // but we're already initialized — override so AuthGate can redirect
          setInitializing(false)
          // Set the session-expired banner if (a) user had an active session
          // AND (b) the signout wasn't user-initiated AND (c) we haven't
          // already handled this event-fanout in another subscriber within
          // the last 5s. The banner lives on the welcome/login screen via
          // useAuthStore.sessionExpired so the user gets contextual signal
          // when they land there.
          //
          // No toast: the toast was redundant with the banner AND was firing
          // in spurious situations (multi-subscriber fan-out resetting the
          // intentional flag, query errors after signOut surfacing as toasts,
          // etc). The banner alone is the cleaner signal.
          if (wasAuthenticated && !userInitiatedSignOut) {
            const now = Date.now()
            if (now - lastSessionExpiredHandledAt > 5000) {
              lastSessionExpiredHandledAt = now
              setSessionExpired(true)
            }
          }
        }

        if (event === 'PASSWORD_RECOVERY') {
          // User clicked password reset link from email — session is set,
          // redirect to reset-password screen handled by deep link routing.
          // Block AuthGate redirect so the user stays on reset-password
          // instead of being bounced to (tabs) with the recovery session.
          queryClient.setQueryData(authKeys.session(), session)
          setCompletingSignup(true)
        }

        if (event === 'INITIAL_SESSION') {
          hadAuthenticatedSession = !!session?.user
          if (session?.user) {
            userInitiatedSignOut = false
            setSessionExpired(false)
            // Un arranque en frío con sesión viva también entra en la ventana:
            // sin esto, la OTA pendiente recargaba 500 ms después de abrir la
            // app en /explore — el mismo reinicio, apenas al otro lado.
            useAuthStore.getState().markAuthenticated(Date.now())
            // Seed the switch tracker so a later SIGNED_IN for a different
            // account is recognized as a switch (and wipes A's caches).
            lastAuthenticatedUserId = session.user.id
            // El arranque en frio es el UNICO momento que ve la secuencia
            // "sesion vencida -> app cerrada -> entra otra persona".
            conciliarDuenoDeBorradores(session.user.id)
            securityTelemetryService.recordAuthEvent(
              'initial_session',
              session.user.id,
              authProvider(session),
            )
          }
          // Null-safe, same invariant as reconcileSessionCache: a cold start
          // that delivers a late/null INITIAL_SESSION (SecureStore lag) must
          // NEVER blank an already-live cached session — that empty write is
          // the "app logged me out / flickered" half of a bouncing startup.
          // If the event carries no session but the cache already has one,
          // leave the cache untouched and just release the gate.
          const cachedSession = queryClient.getQueryData<Session | null>(authKeys.session())
          if (session || !cachedSession) {
            queryClient.setQueryData(authKeys.session(), session)
          }
          setInitializing(false)
        }
      })

      // Read-side self-heal: on every foreground, reconcile the session cache
      // with the LIVE client token so a missed/late auth event can't leave the
      // displayed identity stale. Lives inside the SAME singleton block and is
      // torn down by the SAME cleanup, so there is exactly one AppState listener
      // per runtime regardless of how many components consume useAuth(). It only
      // ever promotes the cache to the live session (never writes null), so it
      // cannot cause a spurious logout. Complements supabase.ts's
      // startAutoRefresh (which keeps the token fresh; this syncs the cache).
      const appStateSub = AppState.addEventListener('change', (state) => {
        if (state === 'active') void reconcileSessionCache(queryClient)
      })

      authSubscriptionCleanup = () => {
        subscription.unsubscribe()
        appStateSub.remove()
      }
    }

    return () => {
      authSubscriberCount = Math.max(0, authSubscriberCount - 1)
      if (authSubscriberCount === 0 && authSubscriptionCleanup) {
        authSubscriptionCleanup()
        authSubscriptionCleanup = null
      }
    }
  }, [queryClient, setInitializing, setSessionExpired, setCompletingSignup, resetStore])

  // --- Mutations ---

  const seedSessionCache = useCallback(async () => {
    // Same defense as verifyOtp: never write null over a live SIGNED_IN.
    const session = await authService.getSession()
    if (session) {
      queryClient.setQueryData(authKeys.session(), session)
    }
    return session
  }, [queryClient])

  const signInMutation = useMutation({
    mutationFn: async (input: LoginInput) => {
      await authService.signInWithPassword(input)
      return seedSessionCache()
    },
  })

  const signUpMutation = useMutation({
    mutationFn: (input: RegisterInput) => authService.signUp(input),
  })

  const signInWithGoogleMutation = useMutation({
    mutationFn: async () => {
      const profile = await authService.signInWithGoogle()
      // Web OAuth returns before session exists (redirect). Seed when present.
      await seedSessionCache()
      return profile
    },
  })

  const signInWithAppleMutation = useMutation({
    mutationFn: async () => {
      const profile = await authService.signInWithApple()
      await seedSessionCache()
      return profile
    },
  })

  const signOutMutation = useMutation({
    mutationFn: async () => {
      // Mark the upcoming SIGNED_OUT as intentional so the auth listener
      // suppresses the "sesión expirada" toast + banner (user just clicked
      // logout — obviously the session is over).
      userInitiatedSignOut = true
      return authService.signOut()
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (input: ForgotPasswordInput) => authService.resetPassword(input.email),
  })

  const updatePasswordMutation = useMutation({
    mutationFn: (password: string) => authService.updatePassword(password),
  })

  // ── El código de WhatsApp ────────────────────────────────────────────────
  //
  // Las cuatro mutaciones de abajo hablan con el mismo servicio, y ese servicio
  // ya no es Supabase Auth. Validar un número dejó de ser un efecto secundario
  // de iniciar sesión: es una transacción con su propio resultado, y lo que
  // cambia entre "entrar", "registrarse" y "vincular" es qué se hace DESPUÉS de
  // que el número quedó probado, no cómo se prueba.
  //
  // Por eso `sendOtp` ya no lleva canal ni metadatos ni `createUser`: el canal
  // es WhatsApp, y lo otro solo importa al validar, que es cuando existe alguien
  // del otro lado.
  const sendOtpMutation = useMutation({
    mutationFn: ({ phone }: { phone: string }) => phoneVerificationService.sendOtp(phone),
  })

  const verifyOtpMutation = useMutation({
    mutationFn: ({
      phone,
      code,
      allowCreate,
      metadata,
    }: {
      phone: string
      code: string
      /** Solo el registro crea cuentas. Ver phone-verification.service.ts. */
      allowCreate?: boolean
      metadata?: { preferredLocale?: 'es' | 'en'; acceptedTerms?: boolean }
    }) =>
      phoneVerificationService.verifyOtp(phone, code, {
        allowCreate,
        metadata,
        // Registro crea; login nunca. El servidor lo vuelve a exigir.
        intent: allowCreate ? 'signup' : 'login',
      }),
    onSuccess: async (result) => {
      // La sesión de la RESPUESTA primero. Un getSession() de seguimiento puede
      // ir detrás de la escritura a SecureStore en nativo y contestar null por
      // un instante — y escribir ese null en la caché borra la sesión SIGNED_IN
      // y rebota a welcome a alguien cuya cuenta está viva (arranque en frío y
      // "ya funciona").
      const session = result.session ?? (await authService.getSession())
      if (session) {
        queryClient.setQueryData(authKeys.session(), session)
      }
    },
  })

  const sendPhoneVerificationMutation = useMutation({
    mutationFn: (phone: string) => phoneVerificationService.sendOtp(phone),
  })

  const verifyPhoneChangeMutation = useMutation({
    mutationFn: ({
      phone,
      code,
      intent = 'link',
      proveCurrent = false,
    }: {
      phone: string
      code: string
      /**
       * 'link' cuando la cuenta todavia no tiene numero. 'change' cuando ya
       * tiene otro: ahi el servidor exige haber probado el actual primero.
       */
      intent?: 'link' | 'change'
      /** The first step of a change requires a fresh, persisted OTP proof. */
      proveCurrent?: boolean
    }) => phoneVerificationService.verifyPhoneLink(phone, code, { intent, proveCurrent }),
    // Vincular un teléfono cambia lo que la cuenta puede hacer (reservar,
    // publicar). Se siembra la sesión ya, sin esperar un evento, o la puerta de
    // contactos sigue rebotando a alguien que acaba de verificar.
    onSuccess: (result) => {
      // The service returns the refreshed session, not another read of stale
      // storage. Do not resurrect it if the caller signed out or switched
      // accounts while the network operation was finishing.
      const active = queryClient.getQueryData<Session | null>(authKeys.session())
      if (active?.user.id !== result.session.user.id) {
        throw ServiceError.unauthorized('Account changed before phone link completion')
      }
      queryClient.setQueryData(authKeys.session(), result.session)
      void queryClient.invalidateQueries({ queryKey: authKeys.profile(result.session.user.id) })
    },
  })

  const sendEmailVerificationMutation = useMutation({
    mutationFn: (email: string) => authService.sendEmailVerification(email),
  })

  const verifyEmailChangeMutation = useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      authService.verifyEmailChange(email, code),
    // Same as phone: seed the rotated session so the verified email lands in the
    // cache immediately, independent of the TOKEN_REFRESHED event arriving.
    onSuccess: () => seedSessionCache(),
  })

  const signInReset = signInMutation.reset
  const signUpReset = signUpMutation.reset
  const googleReset = signInWithGoogleMutation.reset
  const appleReset = signInWithAppleMutation.reset
  const otpReset = verifyOtpMutation.reset

  const clearAuthErrors = useCallback(() => {
    signInReset()
    signUpReset()
    googleReset()
    appleReset()
    otpReset()
  }, [signInReset, signUpReset, googleReset, appleReset, otpReset])

  return {
    // State
    user,
    profile: profileQuery.data ?? null,
    isSessionLoading: sessionQuery.isLoading,
    isLoading: sessionQuery.isLoading || (isAuthenticated && profileQuery.isLoading),
    isAuthenticated,
    isInitializing,

    // Mutations
    signIn: signInMutation.mutateAsync,
    signUp: signUpMutation.mutateAsync,
    signInWithGoogle: signInWithGoogleMutation.mutateAsync,
    signInWithApple: signInWithAppleMutation.mutateAsync,
    signOut: signOutMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    updatePassword: updatePasswordMutation.mutateAsync,
    sendOtp: sendOtpMutation.mutateAsync,
    verifyOtp: verifyOtpMutation.mutateAsync,
    sendPhoneVerification: sendPhoneVerificationMutation.mutateAsync,
    verifyPhoneChange: verifyPhoneChangeMutation.mutateAsync,
    sendEmailVerification: sendEmailVerificationMutation.mutateAsync,
    verifyEmailChange: verifyEmailChangeMutation.mutateAsync,

    /**
     * Clears the error state of every credential-entry mutation.
     *
     * Sign-in, sign-up and the social providers are separate mutations that
     * all render into the SAME error banner, but each only clears its own
     * error when it re-runs. Without this, a failed password attempt keeps
     * "Credenciales incorrectas" on screen while the user succeeds through
     * Google or Apple — the screen contradicts itself while letting them in.
     * Every auth entry point must call this before starting an attempt.
     */
    clearAuthErrors,

    // Mutation states
    signInPending: signInMutation.isPending,
    signInError: signInMutation.error,
    signUpPending: signUpMutation.isPending,
    signUpError: signUpMutation.error,
    googlePending: signInWithGoogleMutation.isPending,
    applePending: signInWithAppleMutation.isPending,
    sendOtpPending: sendOtpMutation.isPending,
    verifyOtpPending: verifyOtpMutation.isPending,
    sendEmailVerificationPending: sendEmailVerificationMutation.isPending,
    verifyEmailChangePending: verifyEmailChangeMutation.isPending,
    sendPhoneVerificationPending: sendPhoneVerificationMutation.isPending,
    verifyPhoneChangePending: verifyPhoneChangeMutation.isPending,
    resetPasswordPending: resetPasswordMutation.isPending,
    updatePasswordPending: updatePasswordMutation.isPending,
    updatePasswordError: updatePasswordMutation.error,
  }
}
